import React, { useCallback, useEffect, useMemo, useState } from 'react';
import size from 'lodash/size';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';

import {
  ChangeProductsFiltersFunc,
  FetchProductsCacheKey,
  FetchProductsCacheKeys,
  FetchProductsFilterFields,
  FetchProductsFilterProducts,
  FetchProductsFilters,
  FetchProductsLimit,
  FetchProductsSort
} from '../../../products/productsTypes';
import { TeamNanoID, TeamUUID } from '../../../teams/teamsTypes';
import {
  FetchProductsSetByUserIdScopeType,
  FetchProductsSetsCacheKey,
  FetchProductsSetsCacheKeys
} from '../../../productsSets/productsSetsTypes';
import { ThreeDStockOnProductsSelect } from './ThreeDStockProducts.types';
import { LifestyleDetectionParams } from '../../../lifestyleDetections/lifestyleDetectionsTypes';

import {
  FETCH_PRODUCTS_QUERY,
  FetchProductsQueryResponse
} from '../../../products/queries/fetchProducts.query';
import { CACHE_FETCH_PRODUCTS_QUERY } from '../../../products/queries/fetchCacheProducts.query';
import { CACHE_FETCH_SHOW_PRODUCT_QUERY } from '../../../products/queries/fetchCacheShowProduct.query';
import { FETCH_SHOW_PRODUCT_QUERY } from '../../../products/queries/fetchShowProduct.query';

import { useCurrentUser } from '../../../../auth/hooks/useAuth';
import { useProductsFiltersCache } from '../../../products/hooks/useProductsFiltersCache';
import { useCacheProducts } from '../../../products/hooks/useCacheProducts';
import { useThreeDStockProductsSearch } from './hooks/useThreeDStockProductsSearch';
import { useProductsIndexContentSelectedProducts } from '../../../products/components/contents/ProductsIndexContent/hooks/useProductsIndexContentSelectedProducts';
import { useThreeDStockProductsContentCheckedCategories } from '../../components/contents/ThreeDStockProductsContent/hooks/useThreeDStockProductsContentCheckedCategories';
import { useChangePathQueryFilters } from '../../hooks/useChangePathQueryFilters';
import { useReactQueryState } from '../../../common/reactQuery/hooks/useReactQueryState';

import { ThreeDStockShowLayout } from '../../components/layouts/ThreeDStockShowLayout';

import {
  ThreeDStockHeader,
  ThreeDStockHeaderTeamTypes
} from '../../components/headers/ThreeDStockHeader';
import { ThreeDStockSubHeader } from '../../components/headers/ThreeDStockSubHeader';
import { ThreeDStockProductsContent } from '../../components/contents/ThreeDStockProductsContent';
import { ThreeDStockMenu } from '../../components/menus/ThreeDStockMenu';
import { ThreeDStockModalHeader } from '../../components/headers/ThreeDStockModalHeader';

import { SelectedProductsSidebar } from '../../../selectedProducts/components/sidebars/SelectedProductsSidebar';
import { SelectedProductsFloatingCounter } from '../../../selectedProducts/components/SelectedProductsFloatingCounter';

import { ProductCache } from '../../../products/ProductCache';
import { LifestyleDetectionCache } from '../../../lifestyleDetections/LifestyleDetectionCache';

import { StockTabs } from '../../stockConstants';
import {
  INITIAL_PRODUCTS_SORT,
  ProductsPermissions
} from '../../../products/productsConstants';
import { TeamPath } from '../../../teams/TeamPath';
import { StockPath } from '../../StockPath';

const categoriesFilterFields = [
  FetchProductsFilterFields.PRODUCT_CATEGORY_ID,
  FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS
];

const defaultInitialLimit = 48;

interface ThreeDStockProductsModalProps {
  pageAction?: never;
  isModal: boolean;
}

interface ThreeDStockProductsPageProps {
  pageAction: string;
  isModal?: never;
}

interface ThreeDStockProductsDefaultProps {
  productsCacheKey: FetchProductsCacheKey;
  initialFilters?: FetchProductsFilters;
  initialLimit?: FetchProductsLimit;
  productsSetCacheKey: FetchProductsSetsCacheKey;
  productsSetScope?: FetchProductsSetByUserIdScopeType;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  companyUuid?: TeamUUID;
  onProductsAttach?: ThreeDStockOnProductsSelect;
  onProductsSelect?: ThreeDStockOnProductsSelect;
  onChangeFilters?: (filters?: FetchProductsFilters) => void;
  withTypeFiler?: boolean;
  withSeasonFilter?: boolean;
  team?: ThreeDStockHeaderTeamTypes;
}

type ThreeDStockProductsProps = ThreeDStockProductsDefaultProps &
  (ThreeDStockProductsModalProps | ThreeDStockProductsPageProps);

function ThreeDStockProducts({
  pageAction,
  isModal,
  productsCacheKey,
  initialFilters,
  initialLimit = defaultInitialLimit,
  productsSetCacheKey,
  productsSetScope,
  companyNanoId,
  companyUuid,
  isMyLibrary,
  onProductsAttach,
  onProductsSelect,
  onChangeFilters,
  withTypeFiler,
  withSeasonFilter,
  team
}: ThreeDStockProductsProps) {
  const currentUser = useCurrentUser();

  const { setValue: setSearchImageUrl, clearValue } =
    useReactQueryState<LifestyleDetectionParams>(
      LifestyleDetectionCache.searchImageUrlCacheKey()
    );

  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const handleMenuToggle = useCallback<() => void>(
    () => setIsOpenMenu((prev) => !prev),
    []
  );

  const productsCacheKeys = useMemo<FetchProductsCacheKeys>(
    () => [productsCacheKey],
    [productsCacheKey]
  );
  const productsSetCacheKeys = useMemo<FetchProductsSetsCacheKeys>(
    () => [productsSetCacheKey],
    [productsSetCacheKey]
  );

  const {
    defaultInitialFilters,
    cachedProductsSort,
    cachedProductsFilters,
    changeCachedProductsFilters,
    setCachedProductsSort,
    setCachedProductsFilters,
    resetCachedProductsFiltersAndSort
  } = useProductsFiltersCache({
    cacheKey: productsCacheKey,
    initialFilters,
    ignoredFilterNames: []
  });

  const {
    products,
    productsError,
    productsTotalCount,
    productsFetched,
    productsFetchingNextPage,
    productsFilters,
    productsIsPlaceholderData,
    hasNextProductsPage,
    updateProductCache,
    loadMoreProducts,
    prefetchProduct,
    changeProductsFilters,
    productsSort,
    sortProducts,
    filterProducts,
    productsPage,
    productsLimit
  } = useCacheProducts<FetchProductsQueryResponse>({
    cacheKey: productsCacheKey,
    cacheQuery: CACHE_FETCH_PRODUCTS_QUERY,
    fetchCacheItemQuery: CACHE_FETCH_SHOW_PRODUCT_QUERY,
    fetchItemCacheKey: ProductCache.showCacheKey(),
    fetchItemQuery: FETCH_SHOW_PRODUCT_QUERY,
    initialFilters: cachedProductsFilters,
    initialLimit,
    initialSort: cachedProductsSort,
    query: FETCH_PRODUCTS_QUERY,
    trackTotalHits: true
  });

  const {
    productsSet,
    productsSetError,
    productsSetFetched,
    isOptimisticUpdateData,
    productsSetIsPlaceholderData,
    updateProductsSetCache,
    isSelectedProductsSidebarOpen,
    handleSelectedProductsSidebarOpen,
    handleSelectedProductsSidebarClose
  } = useProductsIndexContentSelectedProducts({
    productsSetScope,
    productsSetCacheKey,
    onProductsSelect
  });

  const { clearCheckedCategories } =
    useThreeDStockProductsContentCheckedCategories();

  const handleResetFilterProducts = useCallback<() => void>(() => {
    clearCheckedCategories();
    resetCachedProductsFiltersAndSort(filterProducts, sortProducts);
  }, [
    clearCheckedCategories,
    filterProducts,
    resetCachedProductsFiltersAndSort,
    sortProducts
  ]);

  const { changePathQueryFilters } = useChangePathQueryFilters({
    currentTab: StockTabs.PRODUCTS,
    companyNanoId,
    isMyLibrary,
    onIndexVisit: isModal ? undefined : handleResetFilterProducts
  });

  const handleChangeProductsFilters = useCallback<ChangeProductsFiltersFunc>(
    (changedFilters, removeFilters) => {
      changeCachedProductsFilters(changedFilters, removeFilters);

      changeProductsFilters(changedFilters, removeFilters);

      if (
        !isEmpty(removeFilters) &&
        categoriesFilterFields.every((field) => removeFilters?.includes(field))
      ) {
        onChangeFilters?.({});

        !isModal && changePathQueryFilters(StockTabs.PRODUCTS);
      }
    },
    [
      changeCachedProductsFilters,
      changePathQueryFilters,
      changeProductsFilters,
      isModal,
      onChangeFilters
    ]
  );

  const handleFilterProducts = useCallback<FetchProductsFilterProducts>(
    (nextFilters) => {
      setCachedProductsFilters(nextFilters);

      return filterProducts(nextFilters);
    },
    [filterProducts, setCachedProductsFilters]
  );

  const handleClearFilterProducts = useCallback<
    (persistFiltersFields?: FetchProductsFilterFields[]) => void
  >(
    (persistFiltersFields) => {
      const persistFilters = reduce(
        persistFiltersFields,
        (result, field) => {
          result[field] = productsFilters?.[field];
          return result;
        },
        { ...defaultInitialFilters }
      );

      return handleFilterProducts(persistFilters);
    },
    [defaultInitialFilters, handleFilterProducts, productsFilters]
  );

  const handleSortProducts = useCallback<(nextSort: FetchProductsSort) => void>(
    (nextSort) => {
      sortProducts(nextSort);
      setCachedProductsSort(nextSort);
    },
    [setCachedProductsSort, sortProducts]
  );

  const changeProductsCategoryFilters = useCallback<
    (tab?: StockTabs, changeFilters?: FetchProductsFilters) => void
  >(
    (tab, changedFilters) => {
      clearCheckedCategories();

      const searchFilters: FetchProductsFilters = {};
      if (productsFilters.nameSkuTags)
        searchFilters.nameSkuTags = productsFilters.nameSkuTags;
      if (productsFilters.withTaskName)
        searchFilters.withTaskName = productsFilters.withTaskName;
      if (productsFilters.similarImage)
        searchFilters.similarImage = productsFilters.similarImage;
      if (productsFilters.similarProduct)
        searchFilters.similarProduct = productsFilters.similarProduct;
      if (productsFilters.similarProductsByCustomArea) {
        searchFilters.similarProductsByCustomArea =
          productsFilters.similarProductsByCustomArea;
      }
      if (productsFilters.similarProductsByObjectInLifestyle) {
        searchFilters.similarProductsByObjectInLifestyle =
          productsFilters.similarProductsByObjectInLifestyle;
      }
      if (productsFilters.searchByDetectedId)
        searchFilters.searchByDetectedId = productsFilters.searchByDetectedId;

      if (tab === StockTabs.PRODUCTS) {
        handleFilterProducts({
          ...defaultInitialFilters,
          ...changedFilters,
          ...searchFilters
        });
        handleSortProducts(INITIAL_PRODUCTS_SORT);

        onChangeFilters?.({ ...changedFilters, ...searchFilters });
      }

      if (!isModal) {
        changePathQueryFilters(tab, { ...changedFilters, ...searchFilters });
      }
    },
    [
      changePathQueryFilters,
      defaultInitialFilters,
      handleFilterProducts,
      handleSortProducts,
      isModal,
      onChangeFilters,
      clearCheckedCategories,
      productsFilters
    ]
  );

  const handleSearchInImage = useCallback<(imageUrl: string) => void>(
    (imageUrl) => {
      setSearchImageUrl({ imageUrl });
    },
    [setSearchImageUrl]
  );

  const { handleSearch, onSearchSimilarImage, onSearchSimilarProduct } =
    useThreeDStockProductsSearch({
      changeProductsFilters: handleChangeProductsFilters,
      sortProducts: handleSortProducts,
      isLibrary: !isEmpty(productsFilters.clientIds)
    });

  useEffect(() => {
    return () => {
      clearValue();
    };
  }, [clearValue]);

  return (
    <ThreeDStockShowLayout
      isModal={isModal}
      action={pageAction}
      header={
        <ThreeDStockHeader
          tab={StockTabs.PRODUCTS}
          withAddButton
          disabled={!productsFetched}
          onSearch={handleSearch}
          onSearchSimilarImage={onSearchSimilarImage}
          onSearchInImage={handleSearchInImage}
          withSimilarImageFilter={currentUser.hasPermissions(
            (isMyLibrary &&
              ProductsPermissions.READ_SELF_COMPANY_PRODUCTS_AI_CLIP_GLOBAL_SEARCH_FILTER_MODAL_BUTTON) ||
              (companyNanoId &&
                ProductsPermissions.READ_OTHER_COMPANY_PRODUCTS_AI_CLIP_GLOBAL_SEARCH_FILTER_MODAL_BUTTON) ||
              ProductsPermissions.READ_PRODUCTS_AI_CLIP_GLOBAL_SEARCH_FILTER_MODAL_BUTTON
          )}
          withSimilarInImageFilter={currentUser.hasPermissions(
            ProductsPermissions.READ_PRODUCTS_SEARCH_IN_IMAGE_MODAL_BUTTON
          )}
          withSuggestInput={currentUser.hasPermissions(
            ProductsPermissions.READ_PRODUCTS_SEARCH_SUGGEST_LIST
          )}
          value={productsFilters.nameSkuTags || ''}
          isLib={!!companyNanoId}
          isMyLibrary={isMyLibrary}
          libHref={
            (isMyLibrary && TeamPath.currentCompanyNewLibraryProducts()) ||
            (companyNanoId &&
              TeamPath.companyNewLibraryProducts(companyNanoId)) ||
            TeamPath.currentCompanyNewLibraryProducts()
          }
          stockHref={
            isMyLibrary || !companyNanoId ? StockPath.newProducts() : null
          }
          team={team}
        />
      }
      modalHeader={
        <ThreeDStockModalHeader
          tab={StockTabs.PRODUCTS}
          disabled={!productsFetched}
          onSearch={handleSearch}
          onSearchInImage={handleSearchInImage}
          onSearchSimilarImage={onSearchSimilarImage}
          withSimilarImageFilter={currentUser.hasPermissions(
            (isMyLibrary &&
              ProductsPermissions.READ_SELF_COMPANY_PRODUCTS_AI_CLIP_GLOBAL_SEARCH_FILTER_MODAL_BUTTON) ||
              (companyNanoId &&
                ProductsPermissions.READ_OTHER_COMPANY_PRODUCTS_AI_CLIP_GLOBAL_SEARCH_FILTER_MODAL_BUTTON) ||
              ProductsPermissions.READ_PRODUCTS_AI_CLIP_GLOBAL_SEARCH_FILTER_MODAL_BUTTON
          )}
          withSimilarInImageFilter={currentUser.hasPermissions(
            ProductsPermissions.READ_PRODUCTS_SEARCH_IN_IMAGE_MODAL_BUTTON
          )}
          withSuggestInput={currentUser.hasPermissions(
            ProductsPermissions.READ_PRODUCTS_SEARCH_SUGGEST_LIST
          )}
          value={productsFilters.nameSkuTags || ''}
          onCategoriesClick={handleMenuToggle}
        />
      }
      subHeader={
        <ThreeDStockSubHeader
          tab={StockTabs.PRODUCTS}
          companyNanoId={companyNanoId}
          isMyLibrary={isMyLibrary}
          onAllClick={handleMenuToggle}
          onSelectedItemsSidebarOpen={handleSelectedProductsSidebarOpen}
          selectedItemsCount={size(productsSet?.selectedProducts)}
        />
      }
      secondaryMenu={
        isOpenMenu ? (
          <ThreeDStockMenu
            showTab={isModal ? StockTabs.PRODUCTS : null}
            onClose={handleMenuToggle}
            changeItemsCategoryFilters={changeProductsCategoryFilters}
            companyNanoId={companyNanoId}
            isMyLibrary={isMyLibrary}
            isModal={isModal}
            clientIds={
              Array.isArray(defaultInitialFilters?.clientIds)
                ? defaultInitialFilters.clientIds
                : undefined
            }
            teamIds={defaultInitialFilters?.teamIds}
          />
        ) : null
      }
      sidebar={
        isSelectedProductsSidebarOpen ? (
          <SelectedProductsSidebar
            productsSet={productsSet}
            productsSetFetched={productsSetFetched}
            productsSetError={productsSetError}
            productsSetIsPlaceholderData={productsSetIsPlaceholderData}
            productsCacheKeys={productsCacheKeys}
            productsSetCacheKeys={productsSetCacheKeys}
            onClose={handleSelectedProductsSidebarClose}
            updateProductsSetCache={updateProductsSetCache}
            withoutBackGround={isModal}
            onCustomAttach={onProductsAttach}
            disabledAttach={isOptimisticUpdateData}
            companyNanoId={companyNanoId}
            companyUuid={companyUuid}
          />
        ) : null
      }
    >
      <ThreeDStockProductsContent
        teamUuid={companyUuid}
        productsSort={productsSort}
        productsCacheKey={productsCacheKey}
        productsFilters={productsFilters}
        initialFilters={defaultInitialFilters}
        defaultProductCategoryId={defaultInitialFilters?.productCategoryId}
        defaultProductParentCategoryIds={
          defaultInitialFilters?.productParentCategoryIds
        }
        sortProducts={sortProducts}
        changeProductsFilters={handleChangeProductsFilters}
        filterProducts={handleFilterProducts}
        changeProductsCategoryFilters={changeProductsCategoryFilters}
        onProductSimilarSearchFilter={onSearchSimilarProduct}
        products={products}
        onProductEditButtonMouseEnter={prefetchProduct}
        productsError={productsError}
        productsFetched={productsFetched}
        productsIsPlaceholderData={productsIsPlaceholderData}
        productsTotalCount={productsTotalCount}
        updateProductCache={updateProductCache}
        hasNextProductsPage={hasNextProductsPage}
        productsPage={productsPage}
        productsLimit={productsLimit}
        loadMoreProducts={loadMoreProducts}
        productsFetchingNextPage={productsFetchingNextPage}
        productsSet={productsSet}
        updateProductsSetCache={updateProductsSetCache}
        productsSetCacheKeys={productsSetCacheKeys}
        clearProductsFilters={handleClearFilterProducts}
        companyNanoId={companyNanoId}
        isMyLibrary={isMyLibrary}
        withTypeFiler={withTypeFiler}
        withSeasonFilter={withSeasonFilter}
        withSearchInImage={currentUser.hasPermissions(
          ProductsPermissions.READ_PRODUCTS_SEARCH_IN_IMAGE_MODAL_BUTTON
        )}
        isModal={isModal}
      />

      {isModal &&
      productsSetFetched &&
      productsSet &&
      !isSelectedProductsSidebarOpen ? (
        <SelectedProductsFloatingCounter
          productsSet={productsSet}
          productsSetCacheKey={productsSetCacheKey}
          isOpen={isSelectedProductsSidebarOpen}
          onOpen={handleSelectedProductsSidebarOpen}
          onClose={handleSelectedProductsSidebarClose}
          updateProductsSetCache={updateProductsSetCache}
        />
      ) : null}
    </ThreeDStockShowLayout>
  );
}

export default ThreeDStockProducts;
